/* eslint-disable @typescript-eslint/no-non-null-assertion */
import * as R from "remeda";
import type {
  ActionConfig,
  ActionReference,
  Member,
  Team,
  TriggerReference,
  ValueDescription_T,
} from "@smartsuite/automation-engine-api";
import type {
  ActionCandidate,
  InputCandidate,
  TriggerCandidate,
} from "../../../../../types/candidate";
import {
  generalTypeFilter,
  isSimpleTextOutput,
  isSupportedOutput,
  isWebhookTextOutput,
  memoizedConvertOutputToDynamicSectionOption,
  nameToInitials,
  ssTypeFilter,
  systemTypeFilter,
} from "../utils";
import type { Fields } from "@smartsuite/smartsuite-client";
import { getOutputDescriptions } from "../../../../../utils/conditions-coverter.hook";
import { t } from "i18next";
import type { FieldTypePillProps } from "@smartsuite/react-ui/lib";
import type { LoopingOptionGroup } from "../MemberEditor/utils";
import { usePillTooltip } from "../../../../../utils/make-tooltip.hook";
import { findMatchedAction } from "../../../../../utils/match-action";
import { rootStore } from "../../../../../stores";
import type { LoopScope } from "../../../../../stores/automation/looping.store";
import { CURRENT_ITEM_OUTPUT_ID } from "../../../../../utils/constants";
import { currentItemOutputsRefPill } from "../utils/current-item-outputs-ref-pill";

export interface TriggerActionOption {
  title: string;
  outputId: string;
  icon?: string;
  hash?: string;
  contextSpecific?: boolean;
}

interface ActionOptionGroup {
  name: string;
  options: TriggerActionOption[];
  action: ActionConfig;
}

export interface EmailPill extends FieldTypePillProps {
  type:
    | "actionOutput"
    | "triggerOutput"
    | "member"
    | "team"
    | "custom"
    | "loop";
  value: string;
}

export const triggerToOptions = (
  trigger: TriggerCandidate,
  generalTypes?: ValueDescription_T[],
  ssTypes?: ValueDescription_T[],
  ssFieldTypes?: Fields[],
  systemAllowedOptions?: string[],
): TriggerActionOption[] =>
  getOutputDescriptions(trigger)
    .filter((output) => {
      if (isSimpleTextOutput(output)) {
        return true;
      }
      if (isWebhookTextOutput(trigger, output)) {
        return true;
      }
      if (output.contextSpecific) {
        return systemTypeFilter(systemAllowedOptions, output);
      }
      if (output.smartsuiteFieldType !== undefined) {
        return ssTypeFilter(
          output.smartsuiteFieldType,
          ssFieldTypes,
          ssTypes,
          output,
        );
      }

      return generalTypeFilter(generalTypes, output);
    })
    .map(
      (output): TriggerActionOption => ({
        title: output.label,
        outputId: output.outputId,
        icon: output.icon,
        contextSpecific: output.contextSpecific,
      }),
    );

interface ConvertersFunctions {
  convertInputToPills: (
    input: InputCandidate,
    members: Member[],
    teams: Team[],
    triggerOptions: TriggerActionOption[],
    actionOptions: ActionOptionGroup[],
    trigger: TriggerCandidate,
    actions: ActionConfig[],
    currentAction?: ActionCandidate,
    loopOptions?: LoopingOptionGroup,
  ) => EmailPill[];
}

export const useConverters = (): ConvertersFunctions => {
  const {
    makeTriggerPillTooltip,
    makeActionPillTooltip,
    makeCurrentItemPillTooltip,
  } = usePillTooltip();

  const convertInputToPills = (
    input: InputCandidate,
    members: Member[],
    teams: Team[],
    triggerOptions: TriggerActionOption[],
    actionOptions: ActionOptionGroup[],
    trigger: TriggerCandidate,
    actions: ActionConfig[],
    currentAction?: ActionCandidate,
    loopOptions?: LoopingOptionGroup,
  ): EmailPill[] =>
    input.value?.values.flatMap((value): EmailPill[] => {
      const rootLooping = rootStore.automations.candidate.looping;
      const looping = currentAction
        ? (rootLooping?.scopeForAction?.(currentAction.hash) ?? rootLooping)
        : rootLooping;
      switch (value.value.oneofKind) {
        case "reference":
          if (value.value.reference.outputId === CURRENT_ITEM_OUTPUT_ID) {
            return [
              {
                type: "loop",
                value: JSON.stringify(value.value.reference),
                icon: "",
                title: t("general.current-item"),
                tooltip: makeCurrentItemPillTooltip(looping),
              },
            ];
          }
          if (value.value.reference.source.oneofKind === "triggerReference") {
            const { outputId, source } = value.value.reference;

            let option = triggerOptions.find(
              (option) =>
                option.outputId === outputId &&
                source.triggerReference.triggerId ===
                  trigger.triggerReference?.triggerId,
            );

            if (option === undefined && loopOptions?.options.length) {
              option = loopOptions.options.find(
                (option) => option.outputId === outputId,
              );
            }
            return [
              {
                type: "triggerOutput",
                value: outputId,
                icon: option?.icon ?? "",
                title:
                  option?.title ??
                  String(t("automation-form.deleted-field-title")),
                isError: !option?.title,
                tooltip: makeTriggerPillTooltip(trigger),
              },
            ];
          } else if (
            value.value.reference.source.oneofKind === "actionReference"
          ) {
            const { outputId } = value.value.reference;
            let option = findActionOption(actionOptions, outputId);

            if (option === undefined && loopOptions?.options.length) {
              option = loopOptions.options.find(
                (option) => option.outputId === outputId,
              );
            }

            const actionId =
              value.value.reference.source.actionReference.actionId;
            const actionOption = actionOptions.find(
              (actionOption) =>
                actionOption.action.actionReference?.actionId === actionId,
            );

            const pillAction = findMatchedAction(
              value.value.reference.source,
              actions,
            );

            if (
              actionOption !== undefined &&
              !isSupportedOutput(
                actionOption.action,
                actionOptions.map((option) => option.action),
                currentAction,
                looping,
              )
            ) {
              return [
                {
                  type: "actionOutput",
                  value: outputId,
                  icon: option?.icon ?? "",
                  title:
                    option?.title ??
                    String(t("automation-form.deleted-field-title")),
                  isError: true,
                  tooltip:
                    pillAction === undefined
                      ? undefined
                      : makeActionPillTooltip(pillAction),
                },
              ];
            }

            if (value.value.reference.outputId === CURRENT_ITEM_OUTPUT_ID) {
              return [
                {
                  type: "loop",
                  value: JSON.stringify(value.value.reference),
                  icon: "",
                  title: t("general.current-item"),
                },
              ];
            }

            if (
              value.value.reference.outputId.startsWith("currentItem|") ||
              value.value.reference.outputId.startsWith("currentItem.")
            ) {
              const outputOptions = memoizedConvertOutputToDynamicSectionOption(
                looping,
                rootStore.automations.candidate.allActions,
                trigger,
              );

              const outputOption = outputOptions?.options.find(
                (option) => option.outputId === outputId,
              );

              return [
                {
                  type: "actionOutput",
                  title:
                    outputOption?.label ??
                    String(t("automation-form.deleted-field-title")),
                  icon: outputOption?.icon ?? "",
                  isError: outputOption?.label === undefined,
                  value: outputId,
                  tooltip:
                    pillAction === undefined
                      ? undefined
                      : makeActionPillTooltip(pillAction),
                } as const,
              ];
            }

            return [
              {
                type: "actionOutput",
                value: outputId,
                icon: option?.icon ?? "",
                title:
                  option?.title ??
                  String(t("automation-form.deleted-field-title")),
                isError: option?.title === undefined,
                tooltip:
                  pillAction === undefined
                    ? undefined
                    : makeActionPillTooltip(pillAction),
              },
            ];
          }
          return [];
        case "function":
          if (value.value.function.function.oneofKind === "memberEmail") {
            const { memberId } = value.value.function.function.memberEmail;
            const member = members.find(
              (member) => member.memberId === memberId,
            );
            const title = R.isTruthy(member)
              ? R.isTruthy(member.name)
                ? member.name
                : member.email[0]
              : "";
            return [
              {
                type: "member",
                value: memberId,
                avatar: {
                  initials: nameToInitials(title),
                  image: member?.profileImageUrl,
                },
                title,
              },
            ];
          } else if (value.value.function.function.oneofKind === "teamEmails") {
            const { teamId } = value.value.function.function.teamEmails;
            const title = R.pipe(
              teams,
              R.find((team) => team.teamId === teamId),
              (team) => team?.name ?? "",
            );
            return [
              {
                type: "team",
                value: teamId,
                avatar: {
                  initials: nameToInitials(title),
                },
                title,
              },
            ];
          } else {
            return [];
          }
        case "string": {
          const { string: email } = value.value;
          return [{ type: "custom", value: email, title: email }];
        }
        default:
          return [];
      }
    }) ?? [];

  return {
    convertInputToPills,
  };
};

const findActionOption = (
  actionOptions: ActionOptionGroup[],
  outputId: string,
): TriggerActionOption | undefined => {
  let result: TriggerActionOption | undefined;
  for (const action of actionOptions) {
    for (const option of action.options) {
      if (option.outputId === outputId) {
        result = option;
        break;
      }
      if (result !== undefined) break;
    }
  }
  return result;
};

export const mergeNextPillToInput = (
  input: InputCandidate,
  pill:
    | {
        type: "triggerOutput";
        triggerReference: TriggerReference;
        outputId: string;
      }
    | {
        type: "actionOutput";
        actionReference: ActionReference;
        outputId: string;
      }
    | {
        type: "member";
        memberId: string;
        memberStatus: string;
      }
    | { type: "team"; teamId: string }
    | {
        type: "custom";
        email: string;
        isEmailValid: (email: string) => boolean;
      }
    | { type: "loop"; looping: LoopScope },
  multiple: boolean,
): InputCandidate => ({
  ...input,
  value: {
    values: [
      ...(multiple && R.isTruthy(input.value) ? input.value.values : []),
      ...(() => {
        switch (pill.type) {
          case "triggerOutput":
            return [
              {
                value: {
                  oneofKind: "reference",
                  reference: {
                    source: {
                      oneofKind: "triggerReference",
                      triggerReference: pill.triggerReference,
                    },
                    outputId: pill.outputId,
                  },
                } as const,
              },
            ];
          case "actionOutput":
            return [
              {
                value: {
                  oneofKind: "reference",
                  reference: {
                    source: {
                      oneofKind: "actionReference",
                      actionReference: pill.actionReference,
                    },
                    outputId: pill.outputId,
                  },
                } as const,
              },
            ];
          case "member":
            return [
              {
                value: {
                  oneofKind: "function",
                  function: {
                    function: {
                      oneofKind: "memberEmail",
                      memberEmail: {
                        memberId: pill.memberId,
                        memberStatus: pill.memberStatus,
                      },
                    },
                  },
                } as const,
              },
            ];
          case "team":
            return [
              {
                value: {
                  oneofKind: "function",
                  function: {
                    function: {
                      oneofKind: "teamEmails",
                      teamEmails: { teamId: pill.teamId },
                    },
                  },
                } as const,
              },
            ];
          case "custom":
            if (multiple) {
              const emails = pill.email.split(/,|, |;|; /);
              return emails
                .filter((email) => pill.isEmailValid(email))
                .map((email) => ({
                  value: {
                    oneofKind: "string",
                    string: email.toLowerCase(),
                  } as const,
                }));
            } else {
              return pill.isEmailValid(pill.email)
                ? [
                    {
                      value: {
                        oneofKind: "string",
                        string: pill.email.toLowerCase(),
                      } as const,
                    },
                  ]
                : [];
            }
          case "loop":
            return currentItemOutputsRefPill(pill.looping);
          default:
            return [];
        }
      })(),
    ],
  },
});

export const ejectPillFromInput = (
  input: InputCandidate,
  index: number,
): InputCandidate => ({
  ...input,
  value: { values: input.value?.values.filter((_, i) => i !== index) ?? [] },
});

type PillInputConfig =
  | {
      type: "actionOutput";
      actionReference: ActionReference;
      outputId: string;
    }
  | {
      type: "triggerOutput";
      triggerReference: TriggerReference;
      outputId: string;
    };

export const handleActionOutput = (
  looping: LoopScope,
  option: { outputId: string },
): PillInputConfig | null => {
  // Handle record list case
  if (
    looping?.source?.oneofKind === "recordList" &&
    looping.source.recordList.recordList.oneofKind === "actionReference"
  ) {
    return {
      type: "actionOutput",
      actionReference: looping.source.recordList.recordList.actionReference,
      outputId: option.outputId,
    };
  }

  // Handle output reference case
  if (
    looping?.source?.oneofKind === "outputReference" &&
    looping.source.outputReference.source.oneofKind === "actionReference" &&
    looping.source.outputReference.source.actionReference
  ) {
    return {
      type: "actionOutput",
      actionReference: looping.source.outputReference.source.actionReference,
      outputId: option.outputId,
    };
  }

  return null;
};

export const handleTriggerOutput = (
  trigger: TriggerCandidate,
  option: { outputId: string },
): PillInputConfig | null => {
  if (R.isTruthy(trigger.triggerReference)) {
    return {
      type: "triggerOutput",
      triggerReference: trigger.triggerReference,
      outputId: option.outputId,
    };
  }
  return null;
};
